import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { motion } from 'framer-motion';

const ResearchVisualizations = () => {
  // Sparse-ViT Performance Data
  const sparseVitData = [
    { name: 'Dense ViT', parameters: 5.53, flops: 912.7, accuracy: 78.2 },
    { name: 'Sparse-ViT', parameters: 2.86, flops: 557.31, accuracy: 79.8 }
  ];

  // SEWP-Net Performance Data
  const sewpNetData = [
    { metric: 'Prediction Accuracy', value: 95 },
    { metric: 'Temporal Prediction', value: 90 },
    { metric: 'Hotspot Detection', value: 95 },
    { metric: 'False Alarm Rate', value: 5 },
    { metric: 'Response Time', value: 92 }
  ];

  // Crowd Density Timeline
  const crowdDensityData = [
    { time: '0s', density: 15, risk: 20 },
    { time: '10s', density: 28, risk: 35 },
    { time: '20s', density: 42, risk: 55 },
    { time: '30s', density: 58, risk: 75 },
    { time: '40s', density: 45, risk: 60 },
    { time: '50s', density: 32, risk: 40 },
    { time: '60s', density: 25, risk: 30 }
  ];

  // Research Impact Metrics
  const researchImpactData = [
    { category: 'Efficiency', score: 95 },
    { category: 'Accuracy', score: 88 },
    { category: 'Robustness', score: 92 },
    { category: 'Scalability', score: 85 },
    { category: 'Interpretability', score: 90 }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Research Impact Visualizations</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Visual insights into the performance and impact of research projects
          </p>
          <div className="w-20 h-1 bg-blue-900 mx-auto mt-6"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Sparse-ViT Comparison */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="border-2 border-gray-100 hover:border-blue-200 transition-all hover:shadow-xl">
              <CardHeader>
                <CardTitle className="text-xl">Sparse-ViT: Efficiency Gains</CardTitle>
                <p className="text-sm text-gray-600">50% reduction in parameters while improving accuracy</p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={sparseVitData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis yAxisId="left" orientation="left" stroke="#3b82f6" />
                    <YAxis yAxisId="right" orientation="right" stroke="#10b981" />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="parameters" fill="#3b82f6" name="Parameters (M)" />
                    <Bar yAxisId="left" dataKey="flops" fill="#60a5fa" name="FLOPs (MMACs)" />
                    <Bar yAxisId="right" dataKey="accuracy" fill="#10b981" name="Accuracy (%)" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-2xl font-bold text-blue-900">-48%</p>
                    <p className="text-sm text-gray-600">Parameter Reduction</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-2xl font-bold text-green-900">+1.6%</p>
                    <p className="text-sm text-gray-600">Accuracy Improvement</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* SEWP-Net Performance Radar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="border-2 border-gray-100 hover:border-blue-200 transition-all hover:shadow-xl">
              <CardHeader>
                <CardTitle className="text-xl">SEWP-Net: System Performance</CardTitle>
                <p className="text-sm text-gray-600">Multi-dimensional evaluation metrics</p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={sewpNetData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="metric" />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} />
                    <Radar name="Performance" dataKey="value" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.6} />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
                <div className="mt-4 bg-purple-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">Key Achievement</p>
                  <p className="text-lg font-semibold text-purple-900">95% Accuracy in Spatial Hotspot Detection</p>
                  <p className="text-xs text-gray-500 mt-1">MAE: 7.41 (~0.1% deviation)</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Crowd Density Timeline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Card className="border-2 border-gray-100 hover:border-blue-200 transition-all hover:shadow-xl">
              <CardHeader>
                <CardTitle className="text-xl">Real-Time Crowd Monitoring</CardTitle>
                <p className="text-sm text-gray-600">Density and risk prediction over time</p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={crowdDensityData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="density" stroke="#f59e0b" strokeWidth={2} name="Crowd Density" />
                    <Line type="monotone" dataKey="risk" stroke="#ef4444" strokeWidth={2} name="Risk Level" />
                  </LineChart>
                </ResponsiveContainer>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <div className="bg-green-50 p-2 rounded text-center">
                    <p className="text-xs text-gray-600">Normal</p>
                    <p className="font-bold text-green-900">0-33</p>
                  </div>
                  <div className="bg-yellow-50 p-2 rounded text-center">
                    <p className="text-xs text-gray-600">Warning</p>
                    <p className="font-bold text-yellow-900">34-66</p>
                  </div>
                  <div className="bg-red-50 p-2 rounded text-center">
                    <p className="text-xs text-gray-600">Critical</p>
                    <p className="font-bold text-red-900">67-100</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Research Impact Radar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Card className="border-2 border-gray-100 hover:border-blue-200 transition-all hover:shadow-xl">
              <CardHeader>
                <CardTitle className="text-xl">Overall Research Impact</CardTitle>
                <p className="text-sm text-gray-600">Comprehensive evaluation across key dimensions</p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={researchImpactData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="category" />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} />
                    <Radar name="Impact Score" dataKey="score" stroke="#1e40af" fill="#1e40af" fillOpacity={0.6} />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
                <div className="mt-4 bg-blue-50 p-3 rounded-lg">
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-600">Average Impact Score</p>
                    <p className="text-3xl font-bold text-blue-900">90%</p>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div className="bg-blue-900 h-2 rounded-full" style={{ width: '90%' }}></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ResearchVisualizations;
